import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, ExternalLink, BarChart3, Tag, Inbox, Quote } from "lucide-react";
import { BRAND } from "@/lib/constants";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AnalyticsTab from "@/components/admin/AnalyticsTab";
import PlansTab from "@/components/admin/PlansTab";
import EnquiriesTab from "@/components/admin/EnquiriesTab";
import TestimonialsTab from "@/components/admin/TestimonialsTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    if (location.state?.user) return;
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => {
        setUser(res.data);
        setIsAuthenticated(true);
      })
      .catch(() => setIsAuthenticated(false));
  }, [location.state]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      toast.success("Signed out.");
      navigate("/admin", { replace: true, state: null });
    }
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center" data-testid="admin-loading">
        <Loader2 size={22} className="animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#09090B] flex flex-col items-center justify-center px-6 noise-overlay relative">
        <img src={BRAND.logo} alt="JE Training" className="h-28 w-auto object-contain mb-10" />
        <p className="font-display text-[#D4AF37] text-sm font-bold tracking-[0.3em] uppercase mb-3">
          Owner Dashboard
        </p>
        <h1
          data-testid="admin-login-heading"
          className="font-display text-white font-black uppercase tracking-tight leading-none text-4xl sm:text-5xl text-center"
        >
          Private access only.
        </h1>
        <p className="mt-4 text-zinc-500 text-sm text-center max-w-sm font-light">
          This area is reserved for JE Training. Sign in with your Google
          account to manage pricing, analytics and enquiries.
        </p>
        <button
          onClick={login}
          data-testid="admin-google-login-button"
          className="mt-10 inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#F3CE56] text-zinc-950 px-9 py-4 font-display text-base font-bold uppercase tracking-[0.16em] transition-colors duration-300"
        >
          Sign in with Google
        </button>
        <Link
          to="/"
          data-testid="admin-back-to-site"
          className="mt-6 text-zinc-600 hover:text-zinc-400 text-xs uppercase tracking-[0.2em] transition-colors"
        >
          ← Back to website
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-white font-body" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-zinc-950/85 border-b border-zinc-800">
        <div className="max-w-[1200px] mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={BRAND.logo} alt="JE Training" className="h-12 w-auto object-contain" />
            <span className="hidden sm:inline font-display text-[#D4AF37] text-xs font-bold tracking-[0.3em] uppercase border-l border-zinc-800 pl-4">
              Owner Dashboard
            </span>
          </div>
          <div className="flex items-center gap-3">
            {user?.picture && (
              <img
                src={user.picture}
                alt={user.name || "Owner"}
                data-testid="admin-user-avatar"
                className="h-8 w-8 rounded-full border border-zinc-700 object-cover"
              />
            )}
            <Link
              to="/"
              data-testid="admin-view-site-link"
              className="hidden sm:inline-flex items-center gap-2 text-zinc-400 hover:text-white text-xs uppercase tracking-[0.16em] font-semibold transition-colors"
            >
              <ExternalLink size={14} strokeWidth={1.5} /> View Site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 border border-zinc-700 hover:border-[#D4AF37] text-white px-4 py-2 font-display text-xs font-bold uppercase tracking-[0.14em] transition-colors duration-300"
            >
              <LogOut size={14} strokeWidth={1.5} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-10">
        <h1 className="font-display text-white font-black uppercase tracking-tight leading-none text-4xl sm:text-5xl mb-2">
          Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}<span className="text-[#D4AF37]">.</span>
        </h1>
        <p className="text-zinc-500 text-sm font-light mb-10">
          Your private hub — pricing, performance and enquiries. Visitors can't see any of this.
        </p>

        <Tabs defaultValue="analytics">
          <TabsList className="bg-zinc-900 border border-zinc-800 rounded-none h-auto p-1 flex-wrap">
            <TabsTrigger
              value="analytics"
              data-testid="admin-tab-analytics"
              className="rounded-none font-display font-bold uppercase tracking-[0.12em] text-xs px-5 py-2.5 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-zinc-950"
            >
              <BarChart3 size={14} className="mr-2" /> Analytics
            </TabsTrigger>
            <TabsTrigger
              value="plans"
              data-testid="admin-tab-plans"
              className="rounded-none font-display font-bold uppercase tracking-[0.12em] text-xs px-5 py-2.5 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-zinc-950"
            >
              <Tag size={14} className="mr-2" /> Pricing Plans
            </TabsTrigger>
            <TabsTrigger
              value="enquiries"
              data-testid="admin-tab-enquiries"
              className="rounded-none font-display font-bold uppercase tracking-[0.12em] text-xs px-5 py-2.5 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-zinc-950"
            >
              <Inbox size={14} className="mr-2" /> Enquiries
            </TabsTrigger>
            <TabsTrigger
              value="testimonials"
              data-testid="admin-tab-testimonials"
              className="rounded-none font-display font-bold uppercase tracking-[0.12em] text-xs px-5 py-2.5 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-zinc-950"
            >
              <Quote size={14} className="mr-2" /> Testimonials
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analytics" className="mt-8">
            <AnalyticsTab />
          </TabsContent>
          <TabsContent value="plans" className="mt-8">
            <PlansTab />
          </TabsContent>
          <TabsContent value="enquiries" className="mt-8">
            <EnquiriesTab />
          </TabsContent>
          <TabsContent value="testimonials" className="mt-8">
            <TestimonialsTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
